import type { MatchSnapshot } from './types';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

/**
 * Reads a match snapshot from the public API.
 *
 * The snapshot is served by `GET /live/matches/{matchId}`, which forwards the
 * live match engine's response unchanged. Web used to call the engine's own
 * Cloud Run URL through a same-origin Next rewrite; the proxy exists so clients
 * stop doing that, which is what lets the engine eventually close itself to
 * unauthenticated public traffic (FFH-2223).
 *
 * The engine owns the payload shape and publishes no spec for it, so the proxy
 * types it as an opaque record and forwards it verbatim. `MatchSnapshot` is our
 * reading of that contract, asserted here rather than validated: a field the
 * engine renames would surface as a missing value on the pitch, not as a
 * response-validation failure mid-match.
 */
export class SnapshotError extends Error {
  constructor(message: string, public status: number) {
    super(message);
    this.name = 'SnapshotError';
  }
}

export const fetchSnapshot = async (
  matchId: string,
  accessToken: string
): Promise<MatchSnapshot> => {
  const { data, error, response } = await ffhClient().GET(
    '/live/matches/{matchId}',
    {
      params: { path: { matchId } },
      headers: authHeader(accessToken)
    }
  );

  if (error || !data) {
    throw new SnapshotError(
      `Snapshot fetch failed: ${response?.status ?? 'no response'}`,
      response?.status ?? 0
    );
  }

  return data.data as unknown as MatchSnapshot;
};
